import React, { useState, useRef } from 'react';
import site from '../data/site.json';
import {
  containerClass,
  ctaClass,
  Eyebrow,
  Arrow,
  CheckIcon,
  Spinner,
  FormAlert,
  inputClass,
  labelClass,
} from './ui';
import Picture from './Picture';

export type FlightPackage = (typeof site.packages)[number];

export const packages: FlightPackage[] = site.packages;

export const formatPrice = (price: number) => `${price}€`;

const pricesValidFrom = new Date(`${site.pricesValidFrom}T00:00:00`).toLocaleDateString('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

interface BookingFormData {
  name: string;
  email: string;
  phone: string;
  preferredDate: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  general?: string;
}

const emptyForm: BookingFormData = { name: '', email: '', phone: '', preferredDate: '', message: '' };

const FlightBooking: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLDivElement>(null);

  const selectedPkg = packages.find((pkg) => pkg.id === selectedPackage);

  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
    setShowBookingForm(true);
    setSubmitStatus('idle');
    scrollToForm();
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !selectedPackage) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const packageName = selectedPkg ? `${selectedPkg.title} - ${selectedPkg.duration}` : selectedPackage;
    const packagePrice = selectedPkg ? formatPrice(selectedPkg.price) : '';

    // Build email message with only filled fields
    let emailMessage = `Buchungsanfrage für: ${packageName}${packagePrice ? ` (${packagePrice})` : ''}\n\n`;
    emailMessage += `Name: ${formData.name}\n`;
    emailMessage += `E-Mail: ${formData.email}\n`;

    if (formData.phone && formData.phone.trim()) {
      emailMessage += `Telefon: ${formData.phone}\n`;
    }

    if (formData.preferredDate && formData.preferredDate.trim()) {
      emailMessage += `Wunschtermin: ${formData.preferredDate}\n`;
    }

    if (formData.message && formData.message.trim()) {
      emailMessage += `\nNachricht:\n${formData.message}\n`;
    }

    try {
      const response = await fetch('/contact_form_smtp.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: `Buchungsanfrage: ${packageName}`,
          message: emailMessage,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData(emptyForm);
        setErrors({});
        setShowBookingForm(false);
        scrollToForm();
      } else {
        setSubmitStatus('error');
        setErrors({ general: result.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.' });
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ general: 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowBookingForm(false);
    setFormData(emptyForm);
    setErrors({});
  };

  return (
    <section id="rundflug" className={`${containerClass} pb-2 pt-10 lg:pb-16 lg:pt-[72px]`}>
      <div className="mb-6 lg:mb-9 lg:grid lg:grid-cols-[5fr_7fr] lg:items-end lg:gap-16">
        <div>
          <Eyebrow>Rundflüge &amp; Gastflüge</Eyebrow>
          <h2 className="text-[32px] font-extrabold leading-[1.05] tracking-[-0.02em] lg:text-[52px] lg:leading-none lg:tracking-[-0.025em]">
            Erleben Sie die Freiheit des Fliegens
          </h2>
        </div>
        <div className="mt-3 lg:mt-0">
          <p className="mb-1.5 text-base leading-normal text-gray-600 lg:max-w-[52ch] lg:text-lg">
            Wählen Sie Ihr Flugerlebnis und buchen Sie noch heute Ihren unvergesslichen Rundflug
          </p>
          <p className="text-[13px] italic text-gray-500">Preise gültig für Buchungen ab dem {pricesValidFrom}</p>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
        {packages.map((pkg) => (
          <article key={pkg.id} className="flex flex-col border-t-2 border-gray-900 pt-4 lg:pt-[18px]">
            <div className="mb-3.5 aspect-[3/2] overflow-hidden bg-gray-100 lg:mb-4 lg:aspect-[4/3]">
              <Picture
                src={pkg.image}
                alt={pkg.imageAlt}
                sizes="(min-width: 1024px) 380px, (min-width: 768px) 50vw, 100vw"
                className="block h-full w-full object-cover"
              />
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-[22px] font-extrabold tracking-[-0.01em] lg:text-2xl">{pkg.title}</h3>
              <span className="text-[26px] font-extrabold tracking-[-0.02em] text-blue-600 lg:text-[28px]">
                {formatPrice(pkg.price)}
              </span>
            </div>
            <p className="mt-0.5 text-sm font-medium text-blue-700">{pkg.duration}</p>
            <p className="mb-3 mt-2.5 text-[15px] leading-normal text-gray-600 lg:mb-3.5">{pkg.description}</p>
            <ul className="mb-4 flex flex-1 flex-col gap-1.5 lg:mb-5">
              {pkg.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm text-gray-900">
                  <CheckIcon />
                  {feature}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => handlePackageSelect(pkg.id)}
              aria-controls="buchungsanfrage"
              aria-label={`Jetzt Buchungsanfrage stellen: ${pkg.title}, ${pkg.duration}`}
              className={`${ctaClass} lg:flex lg:w-full lg:justify-between lg:px-4`}
            >
              Jetzt Buchungsanfrage stellen <Arrow />
            </button>
          </article>
        ))}
      </div>

      <div ref={formRef} id="buchungsanfrage">
        {submitStatus === 'success' && (
          <FormAlert type="success" className="mt-10">
            Vielen Dank für Ihre Buchungsanfrage! Wir werden uns so schnell wie möglich bei Ihnen melden, um Ihren Wunschtermin zu bestätigen.
          </FormAlert>
        )}

        {showBookingForm && (
          <div className="mt-10 border-t-2 border-gray-900 pt-6 lg:mt-12 lg:grid lg:grid-cols-[5fr_7fr] lg:gap-16 lg:pt-8">
            <div className="mb-6 lg:mb-0">
              <Eyebrow>Buchungsanfrage</Eyebrow>
              {selectedPkg && (
                <>
                  <h3 className="text-2xl font-extrabold leading-tight tracking-[-0.01em] lg:text-[32px]">
                    {selectedPkg.title} – {selectedPkg.duration}
                  </h3>
                  <p className="mt-2 text-[26px] font-extrabold tracking-[-0.02em] text-blue-600">
                    {formatPrice(selectedPkg.price)}
                  </p>
                </>
              )}
            </div>

            <form onSubmit={handleSubmit} noValidate className="grid gap-3.5 sm:grid-cols-2">
              {errors.general && (
                <FormAlert type="error" className="sm:col-span-2">
                  {errors.general}
                </FormAlert>
              )}

              <div className="sm:col-span-2">
                <label htmlFor="booking-package" className={labelClass}>
                  Gewähltes Paket *
                </label>
                <select
                  id="booking-package"
                  name="package"
                  value={selectedPackage || ''}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className={`${inputClass()} h-12 lg:h-[46px]`}
                  disabled={isSubmitting}
                >
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.title} - {pkg.duration} ({formatPrice(pkg.price)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="booking-name" className={labelClass}>
                  Vor- und Nachname *
                </label>
                <input
                  type="text"
                  name="name"
                  id="booking-name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Max Mustermann"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'booking-name-error' : undefined}
                  className={`${inputClass(!!errors.name)} h-12 lg:h-[46px]`}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p id="booking-name-error" className="mt-1 text-sm text-red-600">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="booking-email" className={labelClass}>
                  E-Mail *
                </label>
                <input
                  type="email"
                  name="email"
                  id="booking-email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="max.mustermann@beispiel.de"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'booking-email-error' : undefined}
                  className={`${inputClass(!!errors.email)} h-12 lg:h-[46px]`}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p id="booking-email-error" className="mt-1 text-sm text-red-600">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="booking-phone" className={labelClass}>
                  Telefon (optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="booking-phone"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+49 123 456789"
                  className={`${inputClass()} h-12 lg:h-[46px]`}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="booking-date" className={labelClass}>
                  Wunschtermin (optional)
                </label>
                <input
                  type="text"
                  name="preferredDate"
                  id="booking-date"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  placeholder="z.B. Samstag, 15. Juni, nachmittags"
                  className={`${inputClass()} h-12 lg:h-[46px]`}
                  disabled={isSubmitting}
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="booking-message" className={labelClass}>
                  Nachricht (optional)
                </label>
                <textarea
                  id="booking-message"
                  name="message"
                  rows={3}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Haben Sie besondere Wünsche oder Fragen?"
                  className={`${inputClass()} resize-y py-3`}
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row">
                <button type="submit" disabled={isSubmitting} className={`${ctaClass} sm:flex-1 lg:flex lg:justify-between`}>
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-3">
                      <Spinner /> Wird gesendet...
                    </span>
                  ) : (
                    <>
                      Buchungsanfrage senden <Arrow />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="min-h-[48px] border border-gray-300 bg-white px-5 text-[15px] font-semibold text-gray-900 transition-colors hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>

              <p className="text-xs leading-normal text-gray-500 sm:col-span-2">
                * Pflichtfelder. Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
              </p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default FlightBooking;
